import React, { useCallback, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
// => Tiptap packages
import { useEditor, EditorContent, Editor, BubbleMenu } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import Bold from '@tiptap/extension-bold';
import Underline from '@tiptap/extension-underline';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import History from '@tiptap/extension-history';
// Custom
import * as Icons from '../icons';
import { LinkModal } from './LinkModal';
import './styles.css';

const TipTapEditor = ({ initialValue, onChange }) => {
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const wscInstanceRef = useRef<any>(null);

    const editor = useEditor({
        extensions: [
            Document,
            History,
            Paragraph,
            Text,
            Link.configure({
                openOnClick: false,
            }),
            Bold,
            Underline,
            Italic,
            Strike,
            Code,
        ],
        content: initialValue || '',
        editorProps: {
            attributes: {
                spellcheck: 'true',
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);
        },
    }) as Editor;
    const [modalIsOpen, setIsOpen] = useState(false);
    const [url, setUrl] = useState<string>('');

    const openModal = useCallback(() => {
        console.log(editor.chain().focus());
        setUrl(editor.getAttributes('link').href);
        setIsOpen(true);
    }, [editor]);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setUrl('');
    }, []);

    const saveLink = useCallback(() => {
        if (url) {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url, target: '_blank' })
                .run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        closeModal();
    }, [editor, url, closeModal]);

    const removeLink = useCallback(() => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        closeModal();
    }, [editor, closeModal]);

    const toggleBold = useCallback(() => {
        editor.chain().focus().toggleBold().run();
    }, [editor]);

    const toggleUnderline = useCallback(() => {
        editor.chain().focus().toggleUnderline().run();
    }, [editor]);

    const toggleItalic = useCallback(() => {
        editor.chain().focus().toggleItalic().run();
    }, [editor]);

    const toggleStrike = useCallback(() => {
        editor.chain().focus().toggleStrike().run();
    }, [editor]);

    const toggleCode = useCallback(() => {
        editor.chain().focus().toggleCode().run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    useEffect(() => {
        if (!editor) return;
        let { from, to } = editor.state.selection;
        editor.commands.setContent(initialValue, false, {
            preserveWhitespace: 'full',
        });
        editor.commands.setTextSelection({ from, to });
    }, [editor, initialValue]);

    // Initialize WProofreader via CDN script (like the official demo)
    // Only enabled when VITE_ENABLE_SPELLCHECKER is set to 'true'
    useEffect(() => {
        const enableSpellchecker = import.meta.env.VITE_ENABLE_SPELLCHECKER === 'true';
        if (!enableSpellchecker || !editor || wscInstanceRef.current) return;

        // Load the WProofreader script from CDN
        const script = document.createElement('script');
        script.src = 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js';
        script.async = true;
        script.onload = () => {
            // Set global config
            (window as any).WEBSPELLCHECKER_CONFIG = {
                autoSearch: true,
                autoDestroy: true,
                autocorrect: true,
                autocomplete: true,
                serviceId: '1:HA5Gl1-qzT7h2-fOLIO1-jdchR-5nVU13-GYHiI-e9dL33-WIm9B-5G5Oo-898DN-cKJmA2-2t1',
                serviceProtocol: 'https',
                servicePort: '443',
                serviceHost: 'svc.webspellchecker.net',
                servicePath: 'api',
                lang: 'en_US',
                theme: 'default',
                localization: 'en-us',
                enableBadgeButton: true,
                globalBadge: false,
                enableLanguagesInBadgeButton: false,
                actionItems: [
                    'ignoreAll',
                    'addWord',
                    'settings',
                    'toggle',
                    'proofreadDialog',
                    'report',
                ],
                settingsSections: [
                    'dictionaries',
                    'languages',
                    'general',
                    'options',
                ],
            };

            // Initialize on the editor's DOM
            if ((window as any).WEBSPELLCHECKER) {
                wscInstanceRef.current = (window as any).WEBSPELLCHECKER.init({
                    container: editor.view.dom,
                });
            }
        };
        document.head.appendChild(script);

        return () => {
            if (wscInstanceRef.current) {
                wscInstanceRef.current.destroy();
                wscInstanceRef.current = null;
            }
            // Clean up script
            script.remove();
        };
    }, [editor]);

    return (
        <div className='editor form-control' ref={editorContainerRef}>
            <div className='menu'>
                <button
                    type='button'
                    className='menu-button'
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                >
                    <Icons.RotateLeft />
                </button>
                <button
                    type='button'
                    className='menu-button'
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                >
                    <Icons.RotateRight />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('link'),
                    })}
                    onClick={openModal}
                >
                    <Icons.Link />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('bold'),
                    })}
                    onClick={toggleBold}
                >
                    <Icons.Bold />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('underline'),
                    })}
                    onClick={toggleUnderline}
                >
                    <Icons.Underline />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('intalic'),
                    })}
                    onClick={toggleItalic}
                >
                    <Icons.Italic />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('strike'),
                    })}
                    onClick={toggleStrike}
                >
                    <Icons.Strikethrough />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('code'),
                    })}
                    onClick={toggleCode}
                >
                    <Icons.Code />
                </button>
            </div>

            <BubbleMenu
                className='bubble-menu-light'
                tippyOptions={{ duration: 150 }}
                editor={editor}
                shouldShow={({ editor, view, state, oldState, from, to }) => {
                    // only show the bubble menu for links.
                    return from === to && editor.isActive('link');
                }}
            >
                <button type='button' className='button' onClick={openModal}>
                    Edit
                </button>
                <button
                    type='button'
                    className='button-remove'
                    onClick={removeLink}
                >
                    Remove
                </button>
            </BubbleMenu>

            <EditorContent editor={editor} />

            <LinkModal
                url={url}
                isOpen={modalIsOpen}
                closeModal={closeModal}
                onChangeUrl={(e) => setUrl(e.target.value)}
                onSaveLink={saveLink}
                onRemoveLink={removeLink}
            />
        </div>
    );
};

export default TipTapEditor;
